/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 */

import { type ReactEventHandler, useCallback, useEffect, useState } from 'react'

interface UseUnloadableMediaIdsProps {
  getIdFromImg: (img: HTMLImageElement) => number
  querySelector?: string
}

export const useUnloadableMediaIds = ({
  getIdFromImg,
  querySelector,
}: UseUnloadableMediaIdsProps) => {
  const [unloadableMediaIds, setUnloadableMediaIds] = useState<ReadonlySet<number>>(new Set([]))
  const addUnloadableMediaId: ReactEventHandler<HTMLImageElement> = useCallback(
    (e) => {
      const mediaId = getIdFromImg(e.currentTarget)
      setUnloadableMediaIds((unloadableMediaIds) =>
        unloadableMediaIds.has(mediaId)
          ? unloadableMediaIds
          : new Set([...unloadableMediaIds, mediaId])
      )
    },
    [getIdFromImg]
  )
  useEffect(() => {
    // An image may fail to load before hydration, in which case we'll miss the `error` event.
    // This effect finds any already-failed images and removes them.
    if (typeof window !== 'undefined' && querySelector) {
      const initialUnloadableMediaIds: number[] = []
      const queryResults = document.querySelectorAll<HTMLImageElement>(querySelector)
      for (const img of queryResults) {
        if (
          // The first image shouldn't be skipped as it is currently hydrating. If the first image errors after hydration it will still trigger addUnloadableMediaId
          img.complete &&
          img.naturalHeight === 0 &&
          img.src &&
          (queryResults[0].dataset.id !== img.dataset.id || queryResults[0].id !== img.id)
        ) {
          initialUnloadableMediaIds.push(getIdFromImg(img))
        }
      }
      if (initialUnloadableMediaIds.length) {
        setUnloadableMediaIds(
          (unloadableMediaIds) => new Set([...unloadableMediaIds, ...initialUnloadableMediaIds])
        )
      }
    }
  }, [getIdFromImg, querySelector])
  return {
    addUnloadableMediaId,
    unloadableMediaIds,
  }
}
